import { useLazyQuery } from '@apollo/client';
import {
    GetClassroomResponse,
    GetClassroomVariables,
    GET_CLASSROOM,
    GetClassroomResultsResponse,
    GetClassroomResultsVariables,
    GET_CLASSROOM_RESULTS,
    GetHomeworkResponse,
    GetHomeworkVariables,
    GetHomeworkResultsResponse,
    GetHomeworkResultsVariables,
    GET_HOMEWORK,
    GET_HOMEWORK_RESULTS,
    GetResultFilesResponse,
    GET_RESULT_FILES,
} from './gql';
import { useCallback, useEffect, useState } from 'react';
import { SlideResult } from '../types/result';
import { isQuestionComponentType, parseSlide } from '@/types/slide';
import { getScore } from '@/utils/score';

export const useSlideResults = (src: { type: 'classroom' | 'homework'; id: string }) => {
    // classrom result gql
    const [getClassroom, { loading: loadingClassroom, data: classroom }] = useLazyQuery<GetClassroomResponse, GetClassroomVariables>(GET_CLASSROOM, {
        variables: { where: { id: src.id } },
        fetchPolicy: 'network-only',
    });
    const [getClassroomResults, { loading: loadingClassroomResults, data: classroomResults }] = useLazyQuery<GetClassroomResultsResponse, GetClassroomResultsVariables>(GET_CLASSROOM_RESULTS, {
        variables: { where: { testroom: { id: { equals: src.id } } } },
        fetchPolicy: 'network-only',
    });

    // homework result gql
    const [getHomework, { loading: loadingHomework, data: homework }] = useLazyQuery<GetHomeworkResponse, GetHomeworkVariables>(GET_HOMEWORK, {
        variables: { where: { id: src.id } },
        fetchPolicy: 'network-only',
    });
    const [getHomeworkResults, { loading: loadingHomeworkResults, data: homeworkResults }] = useLazyQuery<GetHomeworkResultsResponse, GetHomeworkResultsVariables>(GET_HOMEWORK_RESULTS, {
        variables: { where: { homework: { id: { equals: src.id } } } },
        fetchPolicy: 'network-only',
    });

    const [getResultFiles] = useLazyQuery<GetResultFilesResponse>(GET_RESULT_FILES);

    // slide result state
    const [slideResults, setSlideResults] = useState<Record<string, SlideResult>>({});

    // fetch classroom or homework data
    const refetch = useCallback(() => {
        if (src.type === 'classroom') {
            getClassroom();
            getClassroomResults();
        } else {
            getHomework();
            getHomeworkResults();
        }
    }, [getClassroom, getClassroomResults, getHomework, getHomeworkResults, src.type]);

    // init and upon src.type change to refetch student results
    useEffect(() => {
        refetch();
    }, [refetch]);

    // parse classroom or homework data
    useEffect(() => {
        const slides = src.type === 'classroom' ? classroom?.testroom.lessonVersion.slides : homework?.homework.lessonVersion.slides;
        const students = src.type === 'classroom' ? classroom?.testroom.class.students : homework?.homework.class.map((c) => c.students).flat();
        const results = src.type === 'classroom' ? classroomResults?.results : homeworkResults?.homeworkResults;

        if (slides && students && results) {
            const slideResults = slides.reduce(
                (acc, rawSlide) => {
                    const parsedSlide = parseSlide(rawSlide);
                    acc[rawSlide.id] = {
                        id: rawSlide.id,
                        sort: rawSlide.sort,
                        averageScore: 0,
                        questionCount: parsedSlide.data.filter(isQuestionComponentType).length,
                        averageTimeSpent: 0,
                        timeSpent: 0,
                        slides: students.map((student) => {
                            const slide = parseSlide(rawSlide);

                            return {
                                ...slide,
                                student: student,
                                questions: slide.data.filter(isQuestionComponentType).map((question) => {
                                    const result = [...results].filter((r) => r.slide.id === slide.id && student.id === r.student.id && question.id === r.componentId);
                                    return {
                                        ...question,
                                        answers: result
                                            .sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime())
                                            .map((answer) => {
                                                return {
                                                    ...answer,
                                                    fetchResultFiles: () =>
                                                        getResultFiles({
                                                            variables: {
                                                                where: { result: { id: { equals: answer.id } } },
                                                            },
                                                        }).then(async (response) => {
                                                            return response.data?.resultFiles.map((result) => ({ url: result.file.url })) || [];
                                                        }),
                                                };
                                            }),
                                    };
                                }),
                                score: 0,
                            };
                        }),
                    };

                    return acc;
                },
                {} as Record<string, SlideResult>,
            );

            // calculate total time spent
            for (const result of results) {
                const slide = slideResults[result.slide.id];
                if (slide) {
                    slide.timeSpent += new Date(result.endTime).getTime() - new Date(result.startTime).getTime();
                }
            }

            // calculate average time spent based on total time spent
            for (const result of Object.values(slideResults)) {
                if (result.slides.length > 0) {
                    result.averageTimeSpent = result.timeSpent / result.slides.length;
                } else {
                    result.averageTimeSpent = 0;
                }
            }

            // calculate average score
            for (const result of Object.values(slideResults)) {
                for (const slide of result.slides) {
                    slide.score = slide.questions.reduce((acc, question) => {
                        return acc + getScore(question);
                    }, 0);
                }

                if (result.slides.length > 0) {
                    result.averageScore = result.slides.reduce((acc, slide) => acc + slide.score, 0) / result.slides.length;
                } else {
                    result.averageScore = 0;
                }
            }

            setSlideResults(slideResults);
        }
    }, [classroom, classroomResults, getResultFiles, homework, homeworkResults, src.type]);

    return {
        loading: src.type === 'classroom' ? loadingClassroom || loadingClassroomResults : loadingHomework || loadingHomeworkResults,
        results: slideResults,
        refetch: refetch,
    };
};
